import Entities from "./shared/Entities";

export default class ArticlesFilter {
  constructor() {
    this.entities = new Entities("ArticlesFilter", "[data-grid-section]", ArticlesFilter.initSingle, ArticlesFilter.destroySingle);
  }

  static initSingle(sectionEl) {
    const instance = new ArticlesFilterInstance(sectionEl);
    return instance.init();
  }

  static destroySingle({ entityObj }) {
    entityObj?.destroy();
  }
}

class ArticlesFilterInstance {
  constructor(sectionEl) {
    this.sectionEl = sectionEl;

    this.firstContainer = sectionEl.querySelector("[data-container]");

    this.items = this.firstContainer ? Array.from(this.firstContainer.querySelectorAll("[data-grid-item]")) : [];
    this.popularItems = Array.from(sectionEl.querySelectorAll("[data-grid-item-popular]"));
    this.selects = Array.from(sectionEl.querySelectorAll("[data-filter-select]"));
    this.sectionPadding = sectionEl.querySelector("[data-disable-padding]");
    this.showMoreBtnPopular = sectionEl.querySelector("[data-sections-button-popular]");
    this.showMoreBtn = sectionEl.querySelector("[data-sections-button]");
    this.isPrimaryContainer = !!this.sectionEl.querySelector("[data-container]");

    this.visibleLimit = 6;
  }

  init() {
    if (!this.items.length) return;

    this.restoreFromURL();
    this.bindEvents();
    this.applyFilters();

    return {
      destroy: () => this.unbindEvents(),
    };
  }

  restoreFromURL() {
    const params = new URLSearchParams(window.location.search);

    this.selects.forEach((select) => {
      const type = select.dataset.filterSelect;
      const value = params.get(`filter_${type}`);
      if (value) {
        select.value = value;
      }
    });
  }

  updateURL() {
    const params = new URLSearchParams(window.location.search);

    this.selects.forEach((select) => {
      const type = select.dataset.filterSelect;
      const value = select.value;
      const key = `filter_${type}`;

      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
    });

    const newURL = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;

    window.history.replaceState(null, "", newURL);
  }

  bindEvents() {
    this._onChange = () => {
      this.applyFilters();
      this.updateURL();
    };

    this.selects.forEach((select) => {
      select.addEventListener("change", this._onChange);
    });

    if (!this.firstContainer) {
      this.showMoreBtn?.setAttribute("data-active", true);
    }

    this.showMoreBtn?.addEventListener("click", () => {
      this.visibleLimit = Infinity;
      this.showMoreBtn.setAttribute("data-active", "");
      this._lastVisibleItems && this.render(this._lastVisibleItems);
    });

    this.showMoreBtnPopular?.addEventListener("click", () => {
      this.showMoreBtnPopular.setAttribute("data-active", "");

      const container = this.sectionEl.querySelector("[data-container-popular]");
      container?.setAttribute("data-active", "");
    });
  }

  unbindEvents() {
    this.selects.forEach((select) => {
      select.removeEventListener("change", this._onChange);
    });
  }

  setActiveState(hasFilterActive) {
    const container = this.sectionEl.querySelector("[data-container]");
    if (container) {
      if (hasFilterActive) {
        container.setAttribute("data-active", "");
        if (this.sectionPadding) {
          this.sectionPadding.style.paddingBottom = "0px";
        }
      } else {
        container.removeAttribute("data-active");
        if (this.sectionPadding) {
          if (window.innerWidth < 1024) {
            this.sectionPadding.style.paddingBottom = "40px";
          } else {
            this.sectionPadding.style.paddingBottom = "72px";
          }
        }
      }
    }

    // Ukryj/pokaż sekcje z data-grid-section-to-hide
    const sectionsToHide = this.sectionEl.querySelectorAll("[data-grid-section-to-hide]");
    sectionsToHide.forEach((section) => {
      section.style.display = hasFilterActive ? "none" : "";
    });
  }

  getFilters() {
    const filters = {};
    let hasActiveFilter = false;

    this.selects.forEach((select) => {
      const type = select.dataset.filterSelect;
      const value = select.value;

      if (value && type !== "sort") {
        filters[type] = value;
        hasActiveFilter = true;
      }
    });

    const sort = this.selects.find((s) => s.dataset.filterSelect === "sort")?.value || "new";

    return { filters, sort, hasActiveFilter };
  }

  applyFilters() {
    const { filters, sort, hasActiveFilter } = this.getFilters();

    this.visibleLimit = 6;
    this.showMoreBtn?.removeAttribute("data-active");

    let visibleItems = this.items.filter((item) => {
      for (const type in filters) {
        const itemValues = item.dataset[`filter${this.capitalize(type)}`]?.split(",") || [];

        if (!itemValues.includes(filters[type])) {
          return false;
        }
      }

      return true;
    });

    visibleItems.sort((a, b) => {
      const dateA = parseInt(a.dataset.date || 0);
      const dateB = parseInt(b.dataset.date || 0);

      return sort === "old" ? dateA - dateB : dateB - dateA;
    });

    this._lastVisibleItems = visibleItems;

    const countryFilter = this.selects.find((s) => s.dataset.filterSelect === "country")?.value;
    if (countryFilter) {
      const itemsForSchoolFilter = this.items.filter((item) => {
        const countryValues = item.dataset.filterCountry?.split(",") || [];
        return countryValues.includes(countryFilter);
      });
      this.updateSchoolOptions(itemsForSchoolFilter);
    } else {
      this.updateSchoolOptions(this.items);
    }

    let visiblePopularItems = this.popularItems.filter((item) => {
      for (const type in filters) {
        const itemValues = item.dataset[`filter${this.capitalize(type)}`]?.split(",") || [];
        if (!itemValues.includes(filters[type])) return false;
      }
      return true;
    });

    this.render(visibleItems);
    this.updateTOC(visiblePopularItems);
    this.setActiveState(hasActiveFilter);
  }

  updateTOC(visiblePopularItems) {
    const tocLinks = this.sectionEl.querySelectorAll("[data-section-header]");
    const visibleIds = new Set(visiblePopularItems.map((item) => item.id));

    tocLinks.forEach((link) => {
      const targetId = link.getAttribute("href")?.replace("#", "");
      link.style.display = visibleIds.has(targetId) ? "" : "none";
    });
  }

  updateSchoolOptions(visibleItems) {
    const schoolSelect = this.selects.find((s) => s.dataset.filterSelect === "school");
    if (!schoolSelect) return;

    const currentSchool = schoolSelect.value;

    const availableSlugs = new Set();
    visibleItems.forEach((item) => {
      const schools = item.dataset.filterSchool?.split(",") || [];
      schools.forEach((slug) => slug && availableSlugs.add(slug));
    });

    Array.from(schoolSelect.options).forEach((option, index) => {
      if (index === 0) return;
      option.hidden = !availableSlugs.has(option.value);
    });

    if (currentSchool && !availableSlugs.has(currentSchool)) {
      schoolSelect.value = "";
    }
  }

  render(visibleItems) {
    const container = this.items[0]?.parentElement;
    if (!container) return;

    this.items.forEach((item) => {
      item.style.display = "none";
      item.removeAttribute("data-active");
    });

    const itemsToShow = visibleItems.slice(0, this.visibleLimit);

    itemsToShow.forEach((item, index) => {
      item.style.display = "";
      if (index === 0) item.setAttribute("data-active", "");
      container.appendChild(item);
    });

    // Pokaż/ukryj przycisk "więcej"
    if (this.showMoreBtn) {
      if (visibleItems.length > this.visibleLimit) {
        this.showMoreBtn.removeAttribute("data-active");
      } else {
        this.showMoreBtn.setAttribute("data-active", "");
      }
    }
  }

  capitalize(str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}
